import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import { journalFor, saveJournal } from '../../db/journal.js';
import { ScalePicker } from '../ScalePicker.js';
import { SleepStepper } from './JournalDay.js';
import { useToday } from '../../hooks/useToday.js';

/**
 * Der Morgen-Check-in auf „Heute": nur Schlaf und Energie.
 *
 * Genau die zwei Werte, die neben einem schwachen Training stehen. Alles andere
 * — Stimmung, Muskelkater, Notiz, Fotos — liegt einen Tipp weiter im Tagebuch.
 * „Heute" soll morgens im Halbschlaf zeigen, was zu tun ist, nicht ein Formular.
 *
 * Steht ganz oben und ist weg, wenn beide Werte schon da sind — Erledigtes ist
 * auf „Heute" nichts, was jetzt dran ist. Wer gerade ausfüllt, behält die Karte
 * bis zum nächsten Öffnen: Sonst verschwände sie mitten im Nachstellen der
 * Schlafstunden unter dem Daumen.
 */
export function QuickCheckIn({ onOpenJournal }: { onOpenJournal: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const date = useToday();

  const entry = useLiveQuery(() => (date ? journalFor(date, db) : undefined), [date]);
  const complete = !!entry && entry.sleepHours !== null && entry.energy !== null;
  // Beim ersten geladenen Stand entschieden, nicht bei jedem Tipp danach.
  const [shown, setShown] = useState<{ date: string; open: boolean } | null>(null);
  useEffect(() => {
    if (date && entry !== undefined && shown?.date !== date) {
      setShown({ date, open: !complete });
    }
  }, [date, entry, complete, shown]);
  if (!date || !shown || shown.date !== date || !shown.open) return <></>;

  return (
    <section aria-labelledby="checkin-heading" className="kw-tile flex flex-col gap-4">
      <h2 id="checkin-heading" className="kw-kicker">
        {t('journal.heading')}
      </h2>
      <SleepStepper
        value={entry?.sleepHours ?? null}
        onChange={(value) => void saveJournal(date, { sleepHours: value }, db)}
      />
      <ScalePicker
        label={t('journal.energy')}
        low={t('journal.scale.low')}
        high={t('journal.scale.high')}
        value={entry?.energy ?? null}
        onChange={(value) => void saveJournal(date, { energy: value }, db)}
      />
      <button type="button" onClick={onOpenJournal} className="kw-btn kw-btn-quiet kw-btn-small">
        {t('journal.more')}
      </button>
    </section>
  );
}
